import React from 'react';
import { Card, Typography, Button, Space, Tooltip, Alert, Progress, Badge, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ShoppingOutlined,
  EyeOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  UserOutlined
} from '@ant-design/icons';

const { Text } = Typography;

export const StatCard = ({
  title,
  value,
  icon,
  color,
  suffix,
  prefix,
  format = 'default',
  description,
  showOnlyIfValidated = false,
  ventesValidees = 0
}) => {
  if (showOnlyIfValidated && ventesValidees === 0) {
    return (
      <Card style={{ borderRadius: '2px', border: '1px solid #0073ff'}} className="stat-card" bordered={false}>
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className={`icon-container bg-${color}-100`}>
              {React.cloneElement(icon, { className: `text-${color}-600` })}
            </div>
            <div className="ml-4">
              <Text className="text-gray-500 text-sm font-medium">{title}</Text>
              <div className="text-lg text-gray-400 mt-1 italic">
                Non disponible
              </div>
              {description && (
                <Text className="text-gray-400 text-xs mt-1">{description}</Text>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="stat-card" bordered={false} style={{ borderRadius: '2px', border: '1px solid #0073ff'}}>
      <div className="flex items-start justify-between">
        <div className="flex items-center">
          <div className={`icon-container bg-${color}-100`}>
            {React.cloneElement(icon, { className: `text-${color}-600` })}
          </div>
          <div className="ml-4">
            <Text className="text-gray-500 text-sm font-medium">{title}</Text>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              {prefix}
              {format === 'currency'
                ? (value || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 })
                : format === 'number'
                ? (value || 0).toLocaleString('fr-FR')
                : value
              }
              {suffix}
            </div>
            {description && (
              <Text className="text-gray-400 text-xs mt-1">{description}</Text>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const InfoItem = ({ icon, label, value, color = 'blue', action }) => (
  <div className="info-item">
    <div className="flex items-center mb-2">
      <div className={`icon-wrapper bg-${color}-100`}>
        {React.cloneElement(icon, { className: `text-${color}-600` })}
      </div>
      <Text className="text-gray-500 ml-3 font-medium">{label}</Text>
    </div>
    <div className="flex items-center justify-between">
      <Text className="text-gray-800 font-semibold text-lg">{value}</Text>
      {action && (
        <Button type="link" size="small" onClick={action.onClick}>
          {action.icon} {action.text}
        </Button>
      )}
    </div>
  </div>
);

export const TransactionCard = ({
  transaction,
  calculateMontantRestant,
  verifierSiMontantVerseExiste,
  handleValidationPaiement,
  handleMontantVerse,
  viewVenteDetail
}) => {
  // CORRECTION: Appeler la fonction correctement
  const { totalVerse = 0, montantRestant = 0 } = typeof calculateMontantRestant === 'function'
    ? calculateMontantRestant()
    : { totalVerse: 0, montantRestant: 0 };
 
  const hasVersements = typeof verifierSiMontantVerseExiste === 'function'
    ? verifierSiMontantVerseExiste()
    : false;
 
  // CORRECTION: Pour les ventes Payées, forcer montantRestant à 0
  const afficherMontantRestant = transaction.statut === 'Payée' ? 0 : montantRestant;
  const afficherMontantVerse = transaction.statut === 'Payée' ? transaction.montant : totalVerse;
 
  return (
    <Card style={{ borderRadius: '2px', border: '1px solid #0073ff'}} className="transaction-card" hoverable>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center mb-3">
            <div className="mr-3">
              <Badge.Ribbon
                text={transaction.statut}
                color={transaction.statut === 'Payée' ? 'green' : transaction.statut === 'En attente' ? 'orange' : 'blue'}
              >
                <div className="p-2 bg-blue-50 rounded-lg">
                  <ShoppingOutlined className="text-blue-600 text-xl" />
                </div>
              </Badge.Ribbon>
            </div>
            <div>
              <Text strong className="text-lg block">{transaction.designation}</Text>
              <Text className="text-gray-500 text-sm">{transaction.numero}</Text>
            </div>
          </div>
         
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Text className="text-gray-500 text-sm">Date</Text>
              <div className="flex items-center">
                <CalendarOutlined className="text-gray-400 mr-2" />
                <Text strong>{transaction.dateFormatted}</Text>
              </div>
            </div>
            <div>
              <Text className="text-gray-500 text-sm">Chauffeur</Text>
              <div className="flex items-center">
                <UserOutlined className="text-gray-400 mr-2" />
                <Text strong>{transaction.chauffeur}</Text>
              </div>
            </div>
          </div>
         
          {/* Afficher montant versé et restant pour les ventes Validées ET Payées */}
          {(transaction.statut === 'Validée' || transaction.statut === 'Payée') && (
            <div className="mb-3 p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Text className="text-gray-500 text-sm">Montant Total</Text>
                  <div className="text-lg font-bold text-blue-600">
                    {(transaction.montant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </div>
                </div>
                <div>
                  <Text className="text-gray-500 text-sm">Versé</Text>
                  <div className="text-lg font-bold text-green-600">
                    {afficherMontantVerse.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </div>
                </div>
                <div>
                  <Text className="text-gray-500 text-sm">Restant</Text>
                  <div className="text-lg font-bold text-orange-600">
                    {afficherMontantRestant.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </div>
                </div>
              </div>
              <Progress
                percent={Math.round(((afficherMontantVerse) / (transaction.montant || 1)) * 100)}
                size="small"
                className="mt-2"
                strokeColor="#10b981"
              />
              {transaction.statut === 'Payée' && (
                <div className="text-xs text-green-600 mt-1 text-center">
                  <CheckCircleOutlined /> Paiement complet
                </div>
              )}
            </div>
          )}
         
          {transaction.note && (
            <Alert
              message={transaction.note}
              type="info"
              showIcon
              className="mb-3"
              size="small"
            />
          )}
        </div>
       
        <div className="text-right ml-4">
          <div className="mb-2">
            <Text className="text-gray-500 text-sm">Montant Total</Text>
            <div className={`text-2xl font-bold ${
              transaction.statut === 'Payée' ? 'text-green-600' :
              transaction.statut === 'En attente' ? 'text-orange-600' :
              'text-blue-600'
            }`}>
              {transaction.statut === 'En attente' ? (
                <div className="text-gray-400 italic">Non validé</div>
              ) : (
                (transaction.montant || 0).toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'XOF',
                  minimumFractionDigits: 0
                })
              )}
            </div>
          </div>
         
          <div className="mb-2">
            <Text className="text-gray-500 text-sm">Quantité</Text>
            <Text strong className="text-lg">{transaction.quantite || 0} tonnes</Text>
          </div>
         
          <Space className="mt-4">
            <Tooltip title="Voir détails">
              <Button
                icon={<EyeOutlined />}
                onClick={() => viewVenteDetail && viewVenteDetail(transaction.id)}
                size="small"
              />
            </Tooltip>
           
            {/* Bouton Validation Paiement - s'affiche si des versements ont été faits */}
            {transaction.statut === 'Validée' && montantRestant > 0 && hasVersements && (
              <Tooltip title="Valider le paiement complet">
                <Button
                  type="primary"
                  danger
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleValidationPaiement && handleValidationPaiement(transaction)}
                  size="small"
                >
                  Valider Paiement
                </Button>
              </Tooltip>
            )}
           
            {/* Bouton Montant Versé pour les ventes Validées avec montant restant */}
            {transaction.statut === 'Validée' && montantRestant > 0 && !hasVersements && (
              <Tooltip title="Enregistrer montant versé">
                <Button
                  type="primary"
                  icon={<DollarCircleOutlined />}
                  onClick={() => handleMontantVerse && handleMontantVerse(transaction)}
                  size="small"
                >
                  Montant Versé
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );
};

export const StatusTag = ({ statut }) => {
  let color = 'default';
  let icon = null;
  let text = statut;
 
  switch (statut) {
    case 'Validée':
      color = 'processing';
      icon = <CheckCircleOutlined />;
      text = 'Validée';
      break;
    case 'Payée':
      color = 'success';
      icon = <CheckCircleOutlined />;
      text = 'Payée';
      break;
    case 'En attente':
      color = 'warning';
      icon = <ClockCircleOutlined />;
      text = 'En attente';
      break;
    case 'Annulée':
      color = 'error';
      icon = <CloseCircleOutlined />;
      text = 'Annulée';
      break;
    default:
      color = 'default';
      icon = <ExclamationCircleOutlined />;
  }
 
  return (
    <Tag color={color} icon={icon} className="font-medium px-3 py-1">
      {text}
    </Tag>
  );
};
